/**
 * Tablero Kanban (copiado de timeflow/board-view.tsx): 4 columnas fijas por
 * estado, drag entre columnas + reorden dentro de la columna con posición
 * fraccional (punto medio entre vecinos). El estado local `items` espeja la
 * query y se resincroniza por `signature` para que el SortableContext no
 * parpadee mientras vuelve el refetch.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useTaskMutations } from '@/hooks/useInternalTasks';
import {
  TASK_STATUSES,
  TASK_STATUS_COLOR,
  TASK_STATUS_LABEL,
  type Task,
  type TaskProject,
  type TaskStatus,
} from '@/../../shared/internal-tasks';
import { Dot } from './task-fields';
import { SortableCard, TaskCardBody, type BoardCard } from './TaskCard';
import { NewTaskDialog } from './NewTaskDialog';
import { EmptyState } from '../shared';
import { useTranslation } from 'react-i18next';

const ALL = '__all__';

function isStatus(id: UniqueIdentifier): id is TaskStatus {
  return (TASK_STATUSES as readonly string[]).includes(String(id));
}

export function BoardView({ projects, tasks, onOpenTask }: {
  projects: TaskProject[];
  tasks: Task[];
  onOpenTask: (taskId: string) => void;
}) {
  const { t } = useTranslation();
  const { moveTask, createTask } = useTaskMutations();
  const activeProjects = useMemo(() => projects.filter((p) => p.status === 'active'), [projects]);
  const [projectFilter, setProjectFilter] = useState<string>(ALL);
  const [newOpen, setNewOpen] = useState(false);

  // Si el proyecto filtrado se archivó/borró, volver a "todos".
  useEffect(() => {
    if (projectFilter !== ALL && !activeProjects.some((p) => p.projectId === projectFilter)) setProjectFilter(ALL);
  }, [activeProjects, projectFilter]);

  const cards = useMemo<BoardCard[]>(() => {
    const byId = new Map(activeProjects.map((p) => [p.projectId, p]));
    const subs = new Map<string, { total: number; done: number }>();
    for (const t of tasks) {
      if (!t.parentTaskId) continue;
      const s = subs.get(t.parentTaskId) ?? { total: 0, done: 0 };
      s.total++;
      if (t.status === 'completed') s.done++;
      subs.set(t.parentTaskId, s);
    }
    return tasks
      .filter((task) => !task.parentTaskId && byId.has(task.projectId) && (projectFilter === ALL || task.projectId === projectFilter))
      .map((task) => {
        const p = byId.get(task.projectId)!;
        const s = subs.get(task.taskId) ?? { total: 0, done: 0 };
        return { ...task, projectName: p.name, projectColor: p.color, subtaskCount: s.total, subtaskDone: s.done };
      });
  }, [tasks, activeProjects, projectFilter]);

  const [items, setItems] = useState<BoardCard[]>(cards);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  // dnd-kit dispara el `click` de la card después de soltarla: lo ignoramos un tick.
  const justDragged = useRef(false);
  const openTask = (taskId: string) => { if (!justDragged.current) onOpenTask(taskId); };
  const signature = cards.map((c) => `${c.taskId}:${c.status}:${c.position}:${c.updatedAt}`).join('|');
  useEffect(() => { setItems(cards); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [signature]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const containerOf = (id: UniqueIdentifier | undefined): TaskStatus | null => {
    if (id === undefined) return null;
    if (isStatus(id)) return id;
    return items.find((i) => i.taskId === id)?.status ?? null;
  };
  const byColumn = (status: TaskStatus) =>
    items.filter((i) => i.status === status).sort((a, b) => a.position - b.position);

  function onDragStart(e: DragStartEvent) { setActiveId(e.active.id); }

  function onDragOver(e: DragOverEvent) {
    const { active, over } = e;
    if (!over) return;
    const from = containerOf(active.id);
    const to = containerOf(over.id);
    if (!to || from === to) return;
    setItems((prev) => prev.map((i) => (i.taskId === active.id ? { ...i, status: to } : i)));
  }

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    setActiveId(null);
    justDragged.current = true;
    setTimeout(() => { justDragged.current = false; }, 0);
    if (!over) return;
    const to = containerOf(over.id);
    if (!to) return;

    const column = byColumn(to);
    const without = column.filter((i) => i.taskId !== active.id);
    let index: number;
    if (isStatus(over.id)) index = without.length;
    else {
      const overIdx = without.findIndex((i) => i.taskId === over.id);
      index = overIdx === -1 ? without.length : overIdx;
    }
    const prevPos = index > 0 ? without[index - 1].position : null;
    const nextPos = index < without.length ? without[index].position : null;
    let newPos: number;
    if (prevPos === null && nextPos === null) newPos = 0;
    else if (prevPos === null) newPos = nextPos! - 1;
    else if (nextPos === null) newPos = prevPos + 1;
    else newPos = (prevPos + nextPos) / 2;

    const original = cards.find((c) => c.taskId === active.id);
    setItems((prev) => prev.map((i) => (i.taskId === active.id ? { ...i, status: to, position: newPos } : i)));
    if (original && original.status === to && original.position === newPos) return;
    moveTask.mutate({ taskId: String(active.id), status: to, position: newPos });
  }

  const activeCard = items.find((i) => i.taskId === activeId) ?? null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="h-9 w-[240px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('internal.boardview.todos_los_proyectos')}</SelectItem>
            {activeProjects.map((p) => (
              <SelectItem key={p.projectId} value={p.projectId}>
                <span className="flex items-center gap-2"><Dot color={p.color} />{p.name}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">{items.length} tareas</span>
        <Button size="sm" className="ml-auto" onClick={() => setNewOpen(true)} disabled={activeProjects.length === 0}>
          <Plus className="mr-1 h-4 w-4" /> {t('internal.boardview.nueva_tarea')}
        </Button>
      </div>

      {activeProjects.length === 0 ? (
        <EmptyState
          title={t('internal.boardview.todavia_no_hay_proyectos_activos')}
          description={t('internal.boardview.crea_un_proyecto_en_la_pestana')}
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <div className="flex gap-4 overflow-x-auto pb-4">
            {TASK_STATUSES.map((status) => (
              <Column
                key={status}
                status={status}
                cards={byColumn(status)}
                onOpen={openTask}
                quickAddProjectId={projectFilter === ALL ? null : projectFilter}
                onQuickAdd={(title) =>
                  createTask.mutate({ projectId: projectFilter, title, status })
                }
              />
            ))}
          </div>
          <DragOverlay>{activeCard ? <TaskCardBody card={activeCard} dragging /> : null}</DragOverlay>
        </DndContext>
      )}

      <NewTaskDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        projects={activeProjects}
        defaultProjectId={projectFilter === ALL ? undefined : projectFilter}
      />
    </div>
  );
}

function Column({ status, cards, onOpen, quickAddProjectId, onQuickAdd }: {
  status: TaskStatus;
  cards: BoardCard[];
  onOpen: (taskId: string) => void;
  quickAddProjectId: string | null;
  onQuickAdd: (title: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div className="flex w-72 shrink-0 flex-col">
      <div className="mb-2 flex items-center gap-2 px-1">
        <Dot color={TASK_STATUS_COLOR[status]} />
        <h3 className="text-sm font-medium">{TASK_STATUS_LABEL[status]}</h3>
        <span className="text-xs text-muted-foreground">{cards.length}</span>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          'flex min-h-32 flex-1 flex-col gap-2 rounded-xl bg-muted/40 p-2 transition-colors',
          isOver && 'bg-muted ring-2 ring-primary/30'
        )}
      >
        <SortableContext items={cards.map((c) => c.taskId)} strategy={verticalListSortingStrategy}>
          {cards.map((card) => <SortableCard key={card.taskId} card={card} onOpen={onOpen} />)}
        </SortableContext>
        {quickAddProjectId && <QuickAdd onAdd={onQuickAdd} />}
      </div>
    </div>
  );
}

/** Alta rápida al pie de la columna (solo con un proyecto filtrado). */
function QuickAdd({ onAdd }: { onAdd: (title: string) => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  return (
    <Input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && value.trim()) {
          e.preventDefault();
          onAdd(value.trim());
          setValue('');
        }
      }}
      placeholder={t('internal.boardview.agregar_tarea')}
      className="h-8 border-dashed bg-transparent text-xs"
    />
  );
}
